import { Chip } from "primereact/chip";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import styled from "styled-components";

const MAX_VISIBLE_PAYERS = 3;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
`;

const PayerChip = styled(Chip)`
  &.p-chip {
    font-size: 0.75rem;
    padding: 0 0.5rem;
    border-radius: 8px;
    background: var(--surface-100);
    color: var(--text-color);
  }
  .p-chip-text {
    margin: 0.15rem 0;
    line-height: 1.2;
  }
`;

const PayerCountTag = styled(Tag)`
  &.p-tag.p-component {
    background: var(--primary-50) !important;
    color: var(--primary-700) !important;
    border: 1px solid var(--primary-200) !important;
    font-weight: 600;
    font-size: 0.7rem;
    border-radius: 8px;
  }
`;

interface IPayerChipsCellProps {
  /** The display row's id — used to give this cell's "+N" tooltip a unique target. */
  rowId: string;
  payerNames: string[];
}

/**
 * CLE-2842 — the Payer cell of a bucket row (one rule copied to several payers): the payer count,
 * the first few payers as chips, and a "+N" chip whose tooltip names the rest.
 */
export function PayerChipsCell({ rowId, payerNames }: IPayerChipsCellProps) {
  const visible = payerNames.slice(0, MAX_VISIBLE_PAYERS);
  const hidden = payerNames.slice(MAX_VISIBLE_PAYERS);
  const moreClassName = `payer-chips-more-${rowId.replace(
    /[^a-zA-Z0-9_-]/g,
    ""
  )}`;

  return (
    <Wrapper>
      <PayerCountTag value={`${payerNames.length} payers`} />
      {visible.map((name, index) => (
        // Two payers can share a display name, so the index keeps the keys unique.
        // eslint-disable-next-line react/no-array-index-key
        <PayerChip key={`${name}-${index}`} label={name} />
      ))}
      {hidden.length > 0 && (
        <>
          <PayerChip
            className={moreClassName}
            label={`+${hidden.length}`}
            data-pr-tooltip={hidden.join(", ")}
            data-pr-position="top"
          />
          <Tooltip target={`.${moreClassName}`} />
        </>
      )}
    </Wrapper>
  );
}
